import React, { useEffect, useMemo, useState } from "react";
import { X, Trash2, Info } from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import { fetchCompanyCOA } from "./coaFetch";
import { formatNaira, round2 } from "./faMoney";
import "./fixedAssets.css";

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function DisposeAssetModal({ asset, onClose, onDisposed }) {
  const { companyId } = useCompany();
  const [coa, setCoa] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [disposalDate, setDisposalDate] = useState(todayISO);
  const [proceeds, setProceeds] = useState("0");

  // v2: proceeds account (bank/cash/receivable). Name kept but semantics improved.
  const [proceedsAccountId, setProceedsAccountId] = useState("");
  const [notes, setNotes] = useState("");

  // Preview inputs derived from subledger (dep lines) + asset cost
  const [accumToDate, setAccumToDate] = useState(0);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const data = await fetchCompanyCOA(companyId);
      setCoa(data || []);
    })();
  }, [companyId]);

  // Load depreciation-to-date (<= disposal date) for preview
  useEffect(() => {
    if (!companyId || !asset?.id || !disposalDate) return;

    (async () => {
      setErr("");
      try {
        const { data, error } = await supabase
          .from("fixed_asset_depreciation_lines")
          .select("depreciation_amount, posting_date")
          .eq("company_id", companyId)
          .eq("asset_id", asset.id)
          .lte("posting_date", disposalDate);

        if (error) throw error;

        const sum = round2(
          (data || []).reduce(
            (s, r) => s + Number(r.depreciation_amount || 0),
            0
          )
        );
        setAccumToDate(sum);
      } catch (e) {
        // Preview failure should not block disposal, but display helpful error.
        setAccumToDate(0);
        setErr(e.message || "Failed to compute depreciation-to-date preview.");
      }
    })();
  }, [companyId, asset?.id, disposalDate]);

  const proceedsAccounts = useMemo(() => {
    // More flexible than only "Bank" category:
    // Allow: Bank/Cash/Receivable accounts under Asset type
    const isPreferred = (a) => {
      const name = String(a.name || "").toLowerCase();
      const cat = String(a.category || "").toLowerCase();
      return (
        cat.includes("bank") ||
        name.includes("bank") ||
        cat.includes("cash") ||
        name.includes("cash") ||
        cat.includes("receivable") ||
        name.includes("receivable")
      );
    };

    const allowed = (coa || []).filter((a) => a.type === "Asset");
    return [...allowed].sort(
      (x, y) =>
        Number(isPreferred(y)) - Number(isPreferred(x)) ||
        String(x.name).localeCompare(String(y.name))
    );
  }, [coa]);

  // Auto-pick a proceeds account (best guess)
  useEffect(() => {
    if (proceedsAccountId) return;
    const first = proceedsAccounts[0];
    if (first?.id) setProceedsAccountId(first.id);
  }, [proceedsAccounts, proceedsAccountId]);

  const cost = Number(asset?.cost || 0);
  const nbv = useMemo(
    () => round2(Math.max(0, cost - Number(accumToDate || 0))),
    [cost, accumToDate]
  );
  const proceedsNum = useMemo(() => round2(Number(proceeds || 0)), [proceeds]);
  const gainLoss = useMemo(() => round2(proceedsNum - nbv), [proceedsNum, nbv]);

  async function dispose() {
    setErr("");

    if (!companyId) {
      setErr("Company context not ready.");
      return;
    }
    if (!asset?.id) {
      setErr("Missing asset.");
      return;
    }
    if (!proceedsAccountId) {
      setErr(
        "Please select an account to receive proceeds (Bank/Cash/Receivable)."
      );
      return;
    }
    if (!disposalDate) {
      setErr("Please select disposal date.");
      return;
    }

    const confirmMsg =
      `Dispose asset and post journals?\n\n` +
      `Asset: ${asset.asset_tag} ${asset.name}\n` +
      `Cost: ${cost}\n` +
      `Accum dep (to date): ${accumToDate}\n` +
      `NBV: ${nbv}\n` +
      `Proceeds: ${proceedsNum}\n` +
      `Expected gain/loss: ${gainLoss}\n\n` +
      `This will post: Dr Accum Dep, Cr Fixed Asset, Dr Proceeds, and plug Gain/Loss.`;

    const ok = window.confirm(confirmMsg);
    if (!ok) return;

    setBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/fixed-assets-dispose-v2`;

      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          asset_id: asset.id,
          disposal_date: disposalDate,
          proceeds: proceedsNum,
          proceeds_account_id: proceedsAccountId,
          notes: notes?.trim() || null,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to dispose asset.");

      onDisposed?.(json);
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="fa-panel"
        style={{ width: "min(820px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Dispose Asset</div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              Asset: <b>{asset.asset_tag}</b> — {asset.name}
            </div>
          </div>
          <button className="fa-icon-btn" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* Preview card */}
        <div className="fa-card" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Info size={16} />
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              Preview (based on depreciation posted up to the disposal date)
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 12,
              marginTop: 10,
            }}
          >
            <div>
              <div className="label">Cost</div>
              <div className="value">{formatNaira(cost)}</div>
            </div>
            <div>
              <div className="label">Accum Dep (to date)</div>
              <div className="value">{formatNaira(accumToDate)}</div>
            </div>
            <div>
              <div className="label">NBV</div>
              <div className="value">{formatNaira(nbv)}</div>
            </div>
            <div>
              <div className="label">Expected Gain/Loss</div>
              <div className="value">{formatNaira(gainLoss)}</div>
              <div className="hint" style={{ opacity: 0.8 }}>
                {gainLoss > 0
                  ? "Gain (credited)"
                  : gainLoss < 0
                  ? "Loss (debited)"
                  : "No gain/loss"}
              </div>
            </div>
          </div>
        </div>

        <div className="fa-toolbar" style={{ marginTop: 12 }}>
          <div style={{ minWidth: 240 }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              Disposal Date
            </div>
            <input
              className="fa-input"
              type="date"
              value={disposalDate}
              onChange={(e) => setDisposalDate(e.target.value)}
              disabled={busy}
            />
          </div>

          <div style={{ minWidth: 240 }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              Proceeds (NGN)
            </div>
            <input
              className="fa-input"
              type="number"
              value={proceeds}
              onChange={(e) => setProceeds(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
            Proceeds account (Bank / Cash / Receivable)
          </div>
          <select
            className="fa-select"
            value={proceedsAccountId}
            onChange={(e) => setProceedsAccountId(e.target.value)}
            style={{ width: "100%" }}
            disabled={busy}
          >
            <option value="">Select account…</option>
            {proceedsAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code ? `${a.code} - ` : ""}
                {a.name} ({a.type}/{a.category || "—"})
              </option>
            ))}
          </select>

          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
            Gain/Loss accounts are resolved automatically from category defaults
            (recommended) or by account name (“Gain on Disposal” / “Loss on
            Disposal”).
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
            Notes (optional)
          </div>
          <textarea
            className="fa-textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
          />
        </div>

        {err && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {err}
          </div>
        )}

        <div
          className="fa-actions"
          style={{ marginTop: 12, justifyContent: "flex-end" }}
        >
          <button className="fa-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={dispose}
            disabled={busy}
          >
            <Trash2 size={16} />{" "}
            {busy ? "Disposing…" : "Dispose & Post Journals"}
          </button>
        </div>
      </div>
    </div>
  );
}
